import { useEffect, useMemo, useState } from 'react';
import { Eye, EyeSlash } from 'phosphor-react';
import CryptoJS from 'crypto-js';
import { Supplier } from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import { updateSupplier } from '../../../../../redux/services/suppliersService';
import { getUserDetails } from '../../../../../redux/services/authService';
import { openDillToast } from '../../../../../utils/helpers';
import { DillButton, DillToggle } from '../../../../general/Dill';
import { AppInputField } from '../../../../general/AppInputField/AppInputField';

const encryptSecret = (value: string): string => {
  if (!value.trim()) return '';
  const secretKey = process.env.REACT_APP_CRYPTO_KEY;
  if (!secretKey) return value;
  return CryptoJS.AES.encrypt(JSON.stringify(value), secretKey).toString();
};

const decryptSecret = (value?: string): string => {
  if (!value) return '';
  const secretKey = process.env.REACT_APP_CRYPTO_KEY;
  if (!secretKey) return value;
  try {
    const plainText = CryptoJS.AES.decrypt(value, secretKey)
      .toString(CryptoJS.enc.Utf8)
      .replace(/^"|"$/g, '');
    return plainText || value;
  } catch {
    return value;
  }
};

const RevealField = ({
  label,
  description,
  value,
  onChange,
  secret,
  disabled,
}: {
  label: string;
  description?: string;
  value: string;
  onChange: (v: string) => void;
  secret?: boolean;
  disabled?: boolean;
}) => {
  const [visible, setVisible] = useState(false);

  return (
    <div className="flex flex-col gap-1">
      <p className="text-xs font-semibold text-gray-700 m-0">{label}</p>
      {description && <p className="text-[11px] text-gray-400 m-0">{description}</p>}
      <div className="relative">
        <AppInputField
          id={label}
          value={value}
          onTextChange={onChange}
          inputType={secret && !visible ? 'password' : 'text'}
          isDisabled={disabled}
        />
        {secret && value && (
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-transparent border-none cursor-pointer p-0">
            {visible ? <EyeSlash size={16} /> : <Eye size={16} />}
          </button>
        )}
      </div>
    </div>
  );
};

export function CreditReportSettingsSection({ supplier }: { supplier?: Supplier | null }) {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.user);

  const isDillAdmin = currentUser?.roles?.includes('DILL_ADMIN') ?? false;

  const canEdit = useMemo(() => {
    if (!currentUser?.email || !supplier) return false;
    const email = currentUser.email.toLowerCase();
    const supplierEmail = supplier.email?.toLowerCase() ?? '';
    const adminEmails = (supplier.adminSupplierEmails ?? []).map((e) => e.toLowerCase());
    return email === supplierEmail || adminEmails.includes(email) || isDillAdmin;
  }, [currentUser, supplier, isDillAdmin]);

  // CreditSafe credentials belong to Dill, not the supplier's own account, so when this
  // flag is on suppliers (non DILL_ADMIN viewers) don't see the CreditSafe section at all.
  const hideCreditSafeFromSupplier =
    supplier?.creditReportSettings?.hideCreditSafeCredentialsFromSupplier ?? false;
  const canSeeCreditSafeSection = isDillAdmin || !hideCreditSafeFromSupplier;

  const [nacmUrl, setNacmUrl] = useState(supplier?.creditReportSettings?.nacmCreditReportUrl ?? '');
  const [nacmToken, setNacmToken] = useState(
    decryptSecret(supplier?.creditReportSettings?.nacmCreditReportToken)
  );
  const [allowMonitoring, setAllowMonitoring] = useState(
    supplier?.creditReportSettings?.allowCreditReportMinitoring ?? false
  );

  const [experianUsername, setExperianUsername] = useState(
    supplier?.creditReportSettings?.experianUsername ?? ''
  );
  const [experianPassword, setExperianPassword] = useState(
    decryptSecret(supplier?.creditReportSettings?.experianPassword)
  );
  const [experianClientId, setExperianClientId] = useState(
    decryptSecret(supplier?.creditReportSettings?.experianClientId)
  );
  const [experianClientSecret, setExperianClientSecret] = useState(
    decryptSecret(supplier?.creditReportSettings?.experianClientSecret)
  );
  const [experianSubcode, setExperianSubcode] = useState(
    supplier?.creditReportSettings?.experianSubcode ?? ''
  );
  const [experianModelCode, setExperianModelCode] = useState(
    supplier?.creditReportSettings?.experianModelCode ?? ''
  );

  const [creditSafeUsername, setCreditSafeUsername] = useState(
    supplier?.creditReportSettings?.creditSafeUsername ?? ''
  );
  const [creditSafePassword, setCreditSafePassword] = useState(
    decryptSecret(supplier?.creditReportSettings?.creditSafePassword)
  );
  const [hideCreditSafeFromSupplierValue, setHideCreditSafeFromSupplierValue] = useState(
    supplier?.creditReportSettings?.hideCreditSafeCredentialsFromSupplier ?? false
  );

  useEffect(() => {
    setNacmUrl(supplier?.creditReportSettings?.nacmCreditReportUrl ?? '');
    setNacmToken(decryptSecret(supplier?.creditReportSettings?.nacmCreditReportToken));
    setAllowMonitoring(supplier?.creditReportSettings?.allowCreditReportMinitoring ?? false);
    setExperianUsername(supplier?.creditReportSettings?.experianUsername ?? '');
    setExperianPassword(decryptSecret(supplier?.creditReportSettings?.experianPassword));
    setExperianClientId(decryptSecret(supplier?.creditReportSettings?.experianClientId));
    setExperianClientSecret(decryptSecret(supplier?.creditReportSettings?.experianClientSecret));
    setExperianSubcode(supplier?.creditReportSettings?.experianSubcode ?? '');
    setExperianModelCode(supplier?.creditReportSettings?.experianModelCode ?? '');
    setCreditSafeUsername(supplier?.creditReportSettings?.creditSafeUsername ?? '');
    setCreditSafePassword(decryptSecret(supplier?.creditReportSettings?.creditSafePassword));
    setHideCreditSafeFromSupplierValue(
      supplier?.creditReportSettings?.hideCreditSafeCredentialsFromSupplier ?? false
    );
  }, [supplier]);

  const isDirty =
    nacmUrl !== (supplier?.creditReportSettings?.nacmCreditReportUrl ?? '') ||
    nacmToken !== decryptSecret(supplier?.creditReportSettings?.nacmCreditReportToken) ||
    allowMonitoring !== (supplier?.creditReportSettings?.allowCreditReportMinitoring ?? false) ||
    experianUsername !== (supplier?.creditReportSettings?.experianUsername ?? '') ||
    experianPassword !== decryptSecret(supplier?.creditReportSettings?.experianPassword) ||
    experianClientId !== decryptSecret(supplier?.creditReportSettings?.experianClientId) ||
    experianClientSecret !== decryptSecret(supplier?.creditReportSettings?.experianClientSecret) ||
    experianSubcode !== (supplier?.creditReportSettings?.experianSubcode ?? '') ||
    experianModelCode !== (supplier?.creditReportSettings?.experianModelCode ?? '') ||
    creditSafeUsername !== (supplier?.creditReportSettings?.creditSafeUsername ?? '') ||
    creditSafePassword !== decryptSecret(supplier?.creditReportSettings?.creditSafePassword) ||
    hideCreditSafeFromSupplierValue !==
      (supplier?.creditReportSettings?.hideCreditSafeCredentialsFromSupplier ?? false);

  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const result = await dispatch(
      updateSupplier({
        supplierId: supplier?.id ?? '',
        supplierDetails: {
          creditReportSettings: {
            nacmCreditReportUrl: nacmUrl,
            nacmCreditReportToken: encryptSecret(nacmToken),
            allowCreditReportMinitoring: allowMonitoring,
            experianUsername,
            experianPassword: encryptSecret(experianPassword),
            experianClientId: encryptSecret(experianClientId),
            experianClientSecret: encryptSecret(experianClientSecret),
            experianSubcode,
            experianModelCode,
            creditSafeUsername,
            creditSafePassword: encryptSecret(creditSafePassword),
            hideCreditSafeCredentialsFromSupplier: hideCreditSafeFromSupplierValue,
          },
        },
      })
    );
    setSaving(false);
    if (result.type.includes('fulfilled')) {
      await dispatch(getUserDetails());
      openDillToast({ type: 'SUCCESS', message: 'Credit report settings saved.' });
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to save credit report settings.' });
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden p-4">
      <div className="flex flex-col gap-6">
        <div>
          <p className="text-sm font-semibold text-gray-900 m-0">Credit Report Settings</p>
          <p className="text-xs text-gray-400 mt-0.5">
            Configure credentials for NACM, Experian
            {canSeeCreditSafeSection ? ', and CreditSafe' : ''} credit report integrations.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide m-0">
            NACM (NTCR)
          </p>
          <RevealField label="API URL" value={nacmUrl} onChange={setNacmUrl} disabled={!canEdit} />
          <RevealField
            label="Access Token"
            value={nacmToken}
            onChange={setNacmToken}
            secret
            disabled={!canEdit}
          />
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-xs font-semibold text-gray-700 m-0">Allow Credit Monitoring</p>
              <p className="text-[11px] text-gray-400 mt-0.5 m-0">
                Show the "Save for Monitoring" button on NACM reports
              </p>
            </div>
            <DillToggle
              value={allowMonitoring}
              onChange={setAllowMonitoring}
              isDisabled={!canEdit}
            />
          </div>
        </div>

        <div className="border-t border-gray-100" />

        <div className="flex flex-col gap-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide m-0">
            Experian
          </p>
          <div className="grid grid-cols-2 gap-3">
            <RevealField
              label="Username"
              value={experianUsername}
              onChange={setExperianUsername}
              disabled={!canEdit}
            />
            <RevealField
              label="Password"
              value={experianPassword}
              onChange={setExperianPassword}
              secret
              disabled={!canEdit}
            />
            <RevealField
              label="Client ID"
              value={experianClientId}
              onChange={setExperianClientId}
              disabled={!canEdit}
              secret
            />
            <RevealField
              label="Client Secret"
              value={experianClientSecret}
              onChange={setExperianClientSecret}
              secret
              disabled={!canEdit}
            />
            <RevealField
              label="Subcode"
              description="Experian subscriber code"
              value={experianSubcode}
              onChange={setExperianSubcode}
              disabled={!canEdit}
            />
            <RevealField
              label="Model Code"
              description="Premier Profile scoring model code"
              value={experianModelCode}
              onChange={setExperianModelCode}
              disabled={!canEdit}
            />
          </div>
        </div>

        {canSeeCreditSafeSection && (
          <>
            <div className="border-t border-gray-100" />

            <div className="flex flex-col gap-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide m-0">
                CreditSafe
              </p>
              <div className="grid grid-cols-2 gap-3">
                <RevealField
                  label="Username"
                  value={creditSafeUsername}
                  onChange={setCreditSafeUsername}
                  disabled={!canEdit}
                />
                <RevealField
                  label="Password"
                  value={creditSafePassword}
                  onChange={setCreditSafePassword}
                  secret
                  disabled={!canEdit}
                />
              </div>
              {isDillAdmin && (
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-xs font-semibold text-gray-700 m-0">Hide From Supplier</p>
                    <p className="text-[11px] text-gray-400 mt-0.5 m-0">
                      Hide these CreditSafe credentials from the supplier's own users
                    </p>
                  </div>
                  <DillToggle
                    value={hideCreditSafeFromSupplierValue}
                    onChange={setHideCreditSafeFromSupplierValue}
                  />
                </div>
              )}
            </div>
          </>
        )}

        <div className="flex justify-end pt-1">
          <DillButton
            label="Save Settings"
            onClick={handleSave}
            isLoading={saving}
            isDisabled={!canEdit || !isDirty || saving}
          />
        </div>
      </div>
    </div>
  );
}
